import { compileSchema } from '@toolproof-core/validation/_pure/schema-compilation';

import { composeDirectionCatalogs, parseDirectionCatalog, selectDirectionRuleEffects } from '@/_pure/directionRule';
import { parsePrimitiveDirectionCatalog, PRIMITIVE_DIRECTION_CATALOG } from '@/adapters/primitive/_pure/derivation/primitiveDirectionRules';
import cameraEffectSchema from '@/workflow-runner/_contract/_@schema/directions/cameraEffect.schema.json' with { type: 'json' };
import directionRulesJson from '@/workflow-runner/_data/directions/directionRules.json' with { type: 'json' };
import rodDirectionRulesJson from '@/workflow-runner/_data/directions/rodDirectionRules.json' with { type: 'json' };
import { WORKFLOW_MOMENT_VOCABULARY, WORKFLOW_ROD_MOMENT_VOCABULARY } from './workflowMoment';
import type { Moment } from '@/_contract/_~generated/directionMomentSchema';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { WorkflowCameraDirectionEffect } from '@/workflow-runner/_contract/_~generated/cameraDirectionEffect';

export function parseWorkflowDirectionCatalog(value: unknown): DirectionCatalog<WorkflowCameraDirectionEffect> {
    const validator = compileSchema(cameraEffectSchema);
    return parseDirectionCatalog(value, WORKFLOW_MOMENT_VOCABULARY, effect => {
        const result = validator.validate(effect);
        if (!result.valid) {
            throw new Error(`Invalid workflow camera effect: ${result.issues.map(issue => `${issue.instancePath} ${issue.message}`).join('; ')}`);
        }
        return effect as WorkflowCameraDirectionEffect;
    });
}

export const WORKFLOW_DIRECTION_CATALOG = parseWorkflowDirectionCatalog(directionRulesJson);

export function selectWorkflowCameraDirections(
    moment: Moment,
    catalog: DirectionCatalog<WorkflowCameraDirectionEffect> = WORKFLOW_DIRECTION_CATALOG,
    isIncluded: (key: string) => boolean = () => false,
) {
    const effects = selectDirectionRuleEffects(catalog, { moment }, isIncluded);
    const seen = new Set<WorkflowCameraDirectionEffect['cue']>();
    for (const effect of effects) {
        if (seen.has(effect.cue)) throw new Error(`Multiple workflow camera rules select cue '${effect.cue}' at ${JSON.stringify(moment)}.`);
        seen.add(effect.cue);
    }
    return {
        focus: effects.find(effect => effect.cue === 'replay-focus'),
        completion: effects.find(effect => effect.cue === 'replay-complete'),
        ingress: effects.find(effect => effect.cue === 'panel-ingress'),
        glide: effects.find(effect => effect.cue === 'autoplay-glide'),
    };
}

export const WORKFLOW_ROD_DIRECTION_CATALOG = composeDirectionCatalogs(
    PRIMITIVE_DIRECTION_CATALOG,
    parsePrimitiveDirectionCatalog(rodDirectionRulesJson, WORKFLOW_ROD_MOMENT_VOCABULARY),
);
